// ---------------------------------------------------------------------------
// viewport.js — fabric-native zoom & pan for the Design Editor.
//
// This replaces the old CSS `transform: scale()` on a wrapper div (which gave a
// blurry zoomed view and broke every overlay's coordinate math). Zoom & pan now
// live entirely on the fabric `viewportTransform` [zoom,0,0,zoom,panX,panY], so:
//   - the canvas renders crisply at any zoom,
//   - screen<->object coordinate helpers are exact (rulers / guides / crop use
//     them),
//   - the artboard (the exported W×H region) is object-space (0,0)–(W,H) and
//     everything around it is an implicit pasteboard. The old fixed BLEED inset
//     is gone — dragging an object off the artboard just reveals more pasteboard,
//     and `fit()` leaves a visible gutter via PASTEBOARD_PAD.
//
// Ownership: this module owns wheel-zoom, space-hold+drag pan, middle-mouse pan,
// the container ResizeObserver, and the before/after render painters that draw
// the pasteboard + artboard paper + outline. It exposes an imperative API the
// editor and feature modules call. It does NOT own object:moving (guides) or any
// mode.
// ---------------------------------------------------------------------------

const MIN_ZOOM = 0.02;
const MAX_ZOOM = 16;
const PASTEBOARD_PAD = 48; // px of gutter kept around the artboard on fit()
const PASTEBOARD_COLOR = '#1a1a1a';

export function createViewport(editor) {
  const canvas = editor.canvas;

  let spaceDown = false;
  let panning = false;
  let lastPointer = null;
  let ro = null;
  let resizeTimer = null;

  const clampZoom = (z) => Math.min(Math.max(z, MIN_ZOOM), MAX_ZOOM);
  const getZoom = () => canvas.getZoom();
  const artboard = () => editor.getArtboard();
  const areaSize = () => {
    const el = editor.canvasAreaRef.current;
    return el
      ? { w: el.clientWidth, h: el.clientHeight }
      : { w: canvas.getWidth(), h: canvas.getHeight() };
  };
  const notify = () => { if (editor.onViewportChange) editor.onViewportChange(); };

  // -- checkerboard pattern for the transparent-artboard indicator -----------
  let checkerPattern = null;
  const getChecker = (ctx) => {
    if (checkerPattern) return checkerPattern;
    const size = 16;
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    const cx = c.getContext('2d');
    cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, size, size);
    cx.fillStyle = '#d9d9d9';
    cx.fillRect(0, 0, size / 2, size / 2);
    cx.fillRect(size / 2, size / 2, size / 2, size / 2);
    checkerPattern = ctx.createPattern(c, 'repeat');
    return checkerPattern;
  };

  // ===========================================================================
  // Coordinate helpers (exact under any zoom/pan)
  // ===========================================================================
  const screenToCanvas = (pt) => {
    const el = editor.canvasAreaRef.current;
    const rect = el ? el.getBoundingClientRect() : { left: 0, top: 0 };
    const local = { x: pt.x - rect.left, y: pt.y - rect.top };
    const inv = invertTransform(canvas.viewportTransform);
    return applyTransform(local, inv);
  };
  const canvasToScreen = (pt) => {
    const el = editor.canvasAreaRef.current;
    const rect = el ? el.getBoundingClientRect() : { left: 0, top: 0 };
    const local = applyTransform(pt, canvas.viewportTransform);
    return { x: local.x + rect.left, y: local.y + rect.top };
  };

  // ===========================================================================
  // Zoom
  // ===========================================================================
  const zoomToLocalPoint = (nextZoom, localX, localY) => {
    canvas.zoomToPoint(new editor.fabric.Point(localX, localY), clampZoom(nextZoom));
    notify();
  };
  const zoomToScreenPoint = (nextZoom, clientX, clientY) => {
    const el = editor.canvasAreaRef.current;
    const rect = el ? el.getBoundingClientRect() : { left: 0, top: 0 };
    zoomToLocalPoint(nextZoom, clientX - rect.left, clientY - rect.top);
  };
  const zoomToCenter = (nextZoom) => {
    const { w, h } = areaSize();
    zoomToLocalPoint(nextZoom, w / 2, h / 2);
  };
  // Center the artboard in the viewport at a given zoom (no pointer anchor).
  const applyZoomCentered = (z) => {
    const zoom = clampZoom(z);
    const { width: W, height: H } = artboard();
    const { w, h } = areaSize();
    canvas.setViewportTransform([zoom, 0, 0, zoom, (w - W * zoom) / 2, (h - H * zoom) / 2]);
    notify();
  };
  const fit = () => {
    const { width: W, height: H } = artboard();
    const { w, h } = areaSize();
    if (!W || !H || !w || !h) return;
    const zoom = clampZoom(Math.min((w - 2 * PASTEBOARD_PAD) / W, (h - 2 * PASTEBOARD_PAD) / H));
    applyZoomCentered(zoom);
  };

  // ===========================================================================
  // Render painters (attached by the editor to before/after render)
  // ===========================================================================
  const beforeRender = (opt) => {
    const ctx = opt.ctx;
    if (!ctx) return;
    const exporting = editor.isExporting();
    const { width: W, height: H } = artboard();
    const bg = editor.getArtboardBg();

    if (!exporting) {
      ctx.save();
      ctx.fillStyle = PASTEBOARD_COLOR;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }

    ctx.save();
    const vpt = canvas.viewportTransform;
    ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
    if (!exporting) {
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 24 / (vpt[0] || 1);
      ctx.shadowOffsetY = 6 / (vpt[3] || 1);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
    if (bg) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
    } else if (!exporting) {
      const pat = getChecker(ctx);
      if (pat) { ctx.fillStyle = pat; ctx.fillRect(0, 0, W, H); }
    }
    ctx.restore();
  };

  const afterRender = (opt) => {
    const ctx = opt.ctx;
    if (!ctx || editor.isExporting()) return;
    const { width: W, height: H } = artboard();
    const vpt = canvas.viewportTransform;
    ctx.save();
    ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
    ctx.lineWidth = 1 / (vpt[0] || 1);
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.strokeRect(0.5 / (vpt[0] || 1), 0.5 / (vpt[3] || 1), W, H);
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.strokeRect(0, 0, W, H);
    ctx.restore();
  };

  // ===========================================================================
  // Interaction handlers
  // ===========================================================================
  function resizeToContainer(refit) {
    const el = editor.canvasAreaRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (!w || !h) return;
    canvas.setDimensions({ width: w, height: h });
    if (refit) fit();
    else { canvas.requestRenderAll(); notify(); }
  }

  function onResize() {
    // Preserve zoom/pan on live container resizes; just resize the element.
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => resizeToContainer(false), 60);
  }

  function onWheel(opt) {
    const e = opt.e;
    e.preventDefault();
    e.stopPropagation();
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    zoomToScreenPoint(getZoom() * factor, e.clientX, e.clientY);
  }

  function panActiveNow(opt) {
    const e = opt.e;
    if (e.button === 1) return true;           // middle mouse
    if (spaceDown && e.button === 0) return true; // space-hold + left drag
    return false;
  }

  function onMouseDown(opt) {
    if (!panActiveNow(opt)) return;
    panning = true;
    const e = opt.e;
    lastPointer = { x: e.clientX, y: e.clientY };
    canvas.selection = false;
    canvas.discardActiveObject();
    canvas.setCursor('grabbing');
    canvas.requestRenderAll();
    if (e.preventDefault) e.preventDefault();
  }

  function onMouseMove(opt) {
    if (!panning || !lastPointer) return;
    const e = opt.e;
    const dx = e.clientX - lastPointer.x;
    const dy = e.clientY - lastPointer.y;
    lastPointer = { x: e.clientX, y: e.clientY };
    canvas.relativePan(new editor.fabric.Point(dx, dy));
    canvas.setCursor('grabbing');
    notify();
  }

  function onMouseUp() {
    if (!panning) return;
    panning = false;
    lastPointer = null;
    canvas.selection = editor.isSelectionEnabled();
    canvas.setCursor('default');
    canvas.requestRenderAll();
  }

  function onKeyDown(e) {
    if (e.code !== 'Space') return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (editor.isEditingText()) return;
    spaceDown = true;
    canvas.defaultCursor = 'grab';
    canvas.setCursor('grab');
    e.preventDefault();
  }

  function onKeyUp(e) {
    if (e.code !== 'Space') return;
    spaceDown = false;
    canvas.defaultCursor = 'default';
    if (!panning) canvas.setCursor('default');
  }

  // ===========================================================================
  // Public API
  // ===========================================================================
  return {
    MIN_ZOOM,
    MAX_ZOOM,
    getZoom,
    screenToCanvas,
    canvasToScreen,
    beforeRender,
    afterRender,

    zoomTo: (level, clientPt) => {
      if (clientPt) zoomToScreenPoint(level, clientPt.x, clientPt.y);
      else zoomToCenter(level);
    },
    zoomIn: () => zoomToCenter(getZoom() * 1.2),
    zoomOut: () => zoomToCenter(getZoom() / 1.2),
    zoomActual: () => applyZoomCentered(1),
    fit,
    center: () => applyZoomCentered(getZoom()),
    panBy: (dx, dy) => { canvas.relativePan(new editor.fabric.Point(dx, dy)); notify(); },

    resizeToContainer,

    attach: () => {
      canvas.on('mouse:wheel', onWheel);
      canvas.on('mouse:down', onMouseDown);
      canvas.on('mouse:move', onMouseMove);
      canvas.on('mouse:up', onMouseUp);
      window.addEventListener('keydown', onKeyDown, true);
      window.addEventListener('keyup', onKeyUp, true);
      if (typeof ResizeObserver !== 'undefined' && editor.canvasAreaRef.current) {
        ro = new ResizeObserver(onResize);
        ro.observe(editor.canvasAreaRef.current);
      }
      resizeToContainer(true);
    },

    detach: () => {
      canvas.off('mouse:wheel', onWheel);
      canvas.off('mouse:down', onMouseDown);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:up', onMouseUp);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      clearTimeout(resizeTimer);
      if (ro) { ro.disconnect(); ro = null; }
    },
  };
}

// -- small matrix helpers (kept local so viewport has no import cycle) --------
function applyTransform(pt, m) {
  return {
    x: pt.x * m[0] + pt.y * m[2] + m[4],
    y: pt.x * m[1] + pt.y * m[3] + m[5],
  };
}
function invertTransform(m) {
  const a = m[0], b = m[1], c = m[2], d = m[3], e = m[4], f = m[5];
  const det = a * d - b * c;
  const invDet = det ? 1 / det : 0;
  return [
    d * invDet, -b * invDet, -c * invDet, a * invDet,
    (c * f - d * e) * invDet, (b * e - a * f) * invDet,
  ];
}
